<template>
  <Dialog
    v-model:visible="isVisible"
    modal
    header="Edit Reserves"
    class="w-full max-w-2xl"
    data-test-id="edit-reserves-modal"
  >
    <!-- Loading state: reserve metrics fetch is in flight, no snapshot yet -->
    <div
      v-if="showLoadingState"
      class="flex flex-col gap-4 pt-2 min-h-[350px]"
      data-test-id="edit-reserves-loading-state"
    >
      <Skeleton width="12rem" height="1.5rem" />
      <Skeleton width="100%" height="2.5rem" />
      <Skeleton width="100%" height="2.5rem" />
    </div>

    <!-- Error state: fetch settled but produced no metrics -->
    <div
      v-else-if="showErrorState"
      class="flex flex-col items-start gap-3 pt-2 min-h-[350px]"
      data-test-id="edit-reserves-error-state"
    >
      <p class="text-sm text-surface-700 dark:text-surface-300">
        Couldn't load reserve metrics for this merchant.
      </p>
      <Button
        label="Retry"
        icon="pi pi-refresh"
        size="small"
        severity="secondary"
        @click="onRetryFetch"
        data-test-id="edit-reserves-retry-btn"
      />
    </div>

    <Tabs
      v-else-if="hasAnyAction"
      v-model:value="activeAction"
      class="p-0"
      data-test-id="edit-reserves-tabs"
    >
      <TabList>
        <Tab
          value="setReserve"
          :disabled="!isSetReserveEnabled"
          data-test-id="edit-reserves-tab-set-reserve"
        >
          Set Reserve
        </Tab>
        <Tab
          value="overrideMax"
          :disabled="!isOverrideMaxEnabled"
          data-test-id="edit-reserves-tab-override-max"
        >
          Update Max Amount
        </Tab>
        <Tab
          value="updatePercent"
          :disabled="!isUpdatePercentEnabled"
          data-test-id="edit-reserves-tab-update-percent"
        >
          Update % Holdback
        </Tab>
        <Tab
          value="reEnable"
          :disabled="!isReEnableEnabled"
          data-test-id="edit-reserves-tab-re-enable"
        >
          Re-enable
        </Tab>
      </TabList>

      <TabPanels>
        <!-- Set Reserve -->
        <TabPanel value="setReserve">
          <div class="flex flex-col gap-4 pt-2 min-h-[350px]">
            <SelectButton
              v-model="setReserveMode"
              :options="setReserveModeOptions"
              optionLabel="label"
              optionValue="value"
              :allowEmpty="false"
              size="small"
              data-test-id="set-reserve-mode-selector"
            />

            <div class="min-h-[260px]">
            <!-- Via Persona -->
            <div
              v-if="setReserveMode === 'persona'"
              class="space-y-4"
              data-test-id="set-reserve-persona-form"
            >
              <div class="space-y-2">
                <label
                  for="set-reserve-case-id"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Case ID
                </label>
                <InputText
                  id="set-reserve-case-id"
                  v-model="caseId"
                  placeholder="Persona case: APP-123"
                  class="w-full"
                  data-test-id="set-reserve-case-id-input"
                />
              </div>
              <div class="space-y-2">
                <label
                  for="set-reserve-website-number"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Website Number
                </label>
                <Select
                  input-id="set-reserve-website-number"
                  v-model="websiteNumber"
                  :options="websiteNumberOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="Select"
                  show-clear
                  class="w-40"
                  data-test-id="set-reserve-website-number-select"
                />
              </div>
              <p class="text-xs text-surface-500 dark:text-surface-400">
                Reserve values will be pulled from the linked Persona case.
              </p>
            </div>

            <!-- Manual -->
            <div
              v-if="setReserveMode === 'manual'"
              class="space-y-4"
              data-test-id="set-reserve-manual-form"
            >
              <div class="space-y-2">
                <label
                  for="set-reserve-percent-holdback"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Percent Reserve Holdback
                </label>
                <InputNumber
                  id="set-reserve-percent-holdback"
                  v-model="percentHoldback"
                  @input="onPercentHoldbackInput"
                  :min="1"
                  :max="100"
                  :maxFractionDigits="0"
                  :useGrouping="false"
                  showButtons
                  suffix=" %"
                  placeholder="Enter holdback percentage (1-100)"
                  class="w-full"
                  data-test-id="set-reserve-percent-holdback-input"
                />
              </div>
              <div class="space-y-2">
                <label
                  for="set-reserve-max-amount"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Reserve Max Amount
                </label>
                <InputGroup class="w-full">
                  <InputGroupAddon
                    v-if="reserveCurrency"
                    data-test-id="set-reserve-currency-addon"
                  >
                    {{ reserveCurrency }}
                  </InputGroupAddon>
                  <input
                    id="set-reserve-max-amount"
                    :value="setReserveMaxDisplay"
                    @input="onSetReserveMaxInput"
                    class="p-inputtext p-component w-full"
                    inputmode="decimal"
                    placeholder="Enter max amount"
                    data-test-id="set-reserve-max-amount-input"
                  />
                </InputGroup>
              </div>
            </div>

            </div>
          </div>
        </TabPanel>

        <!-- Override Max Amount -->
        <TabPanel value="overrideMax">
          <div class="flex flex-col gap-4 pt-2 min-h-[350px]">
            <div class="space-y-2">
              <label
                for="override-max-amount"
                class="block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Reserve Max Amount
              </label>
              <InputGroup class="w-full">
                <InputGroupAddon
                  v-if="reserveCurrency"
                  data-test-id="override-max-currency-addon"
                >
                  {{ reserveCurrency }}
                </InputGroupAddon>
                <input
                  id="override-max-amount"
                  :value="overrideMaxDisplay"
                  @input="onOverrideMaxInput"
                  class="p-inputtext p-component w-full"
                  inputmode="decimal"
                  placeholder="Enter max amount"
                  data-test-id="override-max-amount-input"
                />
              </InputGroup>
            </div>
          </div>
        </TabPanel>

        <!-- Update % Holdback -->
        <TabPanel value="updatePercent">
          <div class="flex flex-col gap-4 pt-2 min-h-[350px]">
            <div class="space-y-2">
              <label
                for="update-percent-holdback"
                class="block text-sm font-medium text-surface-700 dark:text-surface-300"
              >
                Percent Reserve Holdback
              </label>
              <InputNumber
                id="update-percent-holdback"
                v-model="updatePercentHoldback"
                @input="onUpdatePercentHoldbackInput"
                :min="1"
                :max="100"
                :maxFractionDigits="0"
                :useGrouping="false"
                showButtons
                suffix=" %"
                placeholder="Enter holdback percentage (1-100)"
                class="w-full"
                data-test-id="update-percent-holdback-input"
              />
            </div>
          </div>
        </TabPanel>

        <!-- Re-enable Reserve -->
        <TabPanel value="reEnable">
          <div class="flex flex-col gap-4 pt-2 min-h-[350px]">
            <p class="text-sm text-surface-700 dark:text-surface-300">
              Re-enable this supplier's reserve with:
            </p>
            <div class="space-y-4" data-test-id="re-enable-form">
              <div class="space-y-2">
                <label
                  for="re-enable-percent-holdback"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Percent Reserve Holdback
                </label>
                <InputNumber
                  id="re-enable-percent-holdback"
                  v-model="reEnablePercentHoldback"
                  @input="onReEnablePercentHoldbackInput"
                  :min="1"
                  :max="100"
                  :maxFractionDigits="0"
                  :useGrouping="false"
                  showButtons
                  suffix=" %"
                  placeholder="Enter holdback percentage (1-100)"
                  class="w-full"
                  data-test-id="re-enable-percent-holdback-input"
                />
              </div>
              <div class="space-y-2">
                <label
                  for="re-enable-max-amount"
                  class="block text-sm font-medium text-surface-700 dark:text-surface-300"
                >
                  Reserve Max Amount
                </label>
                <InputGroup class="w-full">
                  <InputGroupAddon
                    v-if="reserveCurrency"
                    data-test-id="re-enable-currency-addon"
                  >
                    {{ reserveCurrency }}
                  </InputGroupAddon>
                  <input
                    id="re-enable-max-amount"
                    :value="reEnableMaxDisplay"
                    @input="onReEnableMaxInput"
                    class="p-inputtext p-component w-full"
                    inputmode="decimal"
                    placeholder="Enter max amount"
                    data-test-id="re-enable-max-amount-input"
                  />
                </InputGroup>
              </div>
              <p class="text-xs text-surface-500 dark:text-surface-400">
                Pre-filled with the original values — edit either field to
                override.
              </p>
            </div>
          </div>
        </TabPanel>
      </TabPanels>
    </Tabs>

    <template #footer>
      <div class="flex justify-end gap-2 mt-2">
        <Button
          label="Cancel"
          severity="secondary"
          @click="onCancel"
          data-test-id="edit-reserves-cancel-btn"
        />
        <!-- Submit only renders once the snapshot has loaded (tabs visible) —
             during loading and error states, only Cancel is available. A null
             snapshot is a valid "not in reserve table" → Set Reserve state. -->
        <Button
          v-if="snapshotLoaded"
          :label="submitLabel"
          :loading="submitting"
          :disabled="!isValid || submitting"
          @click="onSubmit"
          class="min-w-[11rem]"
          data-test-id="edit-reserves-submit-btn"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, type Ref } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import InputNumber, {
  type InputNumberInputEvent,
} from 'primevue/inputnumber'
import InputGroup from 'primevue/inputgroup'
import InputGroupAddon from 'primevue/inputgroupaddon'
import InputText from 'primevue/inputtext'
import Select from 'primevue/select'
import SelectButton from 'primevue/selectbutton'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primevue'
import { useToast } from 'primevue/usetoast'
import { useFrontegg } from '@frontegg/vue'
import { useFCCMerchantStore } from '@/stores/fcc'
import type { ReserveMetrics } from '@/types/fcc'
import { errorToastWithDetails } from '@/util/toasts'
import { normalizeError } from '@/util/errorNormalizer'
import {
  formatAmount,
  parseAmount,
  restoreCaret,
} from '@/util/fcc/maskedAmount'

type SetReserveMode = 'persona' | 'manual'
type EditReservesAction =
  | 'setReserve'
  | 'overrideMax'
  | 'updatePercent'
  | 'reEnable'

const merchantStore = useFCCMerchantStore()
const { authState } = useFrontegg()
const toast = useToast()

const isVisible = ref(false)
const submitting = ref(false)
// Snapshot of the reserve-metrics record captured at dialog-open time so a
// background refetch (or post-submit refresh) doesn't change tabs
// mid-interaction. Once `snapshotLoaded` is true, `snapshotMetrics` holds the
// settled value: an object (in the reserve table) or null (404 / not in the
// reserve table). `snapshotLoaded` is what distinguishes "loaded, not in
// table" (null) from "not captured yet".
const snapshotMetrics = ref<ReserveMetrics | null>(null)
const snapshotLoaded = ref(false)
const retrying = ref(false)

const activeAction = ref<EditReservesAction>('setReserve')
const setReserveMode = ref<SetReserveMode>('persona')

// Set Reserve — Via Persona inputs
const caseId = ref<string>('')
const websiteNumber = ref<number | null>(null)

// Set Reserve — Manual inputs
const percentHoldback = ref<number | null>(null)
const setReserveMaxAmount = ref<number | null>(null)

// Override Max Amount input
const overrideMaxAmount = ref<number | null>(null)

// Update % Holdback input
const updatePercentHoldback = ref<number | null>(null)

// Re-enable Reserve — percent + max, pre-filled with the existing values so the
// operator can edit either field to override before re-enabling.
const reEnablePercentHoldback = ref<number | null>(null)
const reEnableMaxAmount = ref<number | null>(null)

// Masked bindings for the three currency amount inputs. Each keeps its numeric
// ref (what the API receives) in sync while the field shows a grouped string
// with a stable caret. PrimeVue InputNumber reformats mid-keystroke and jumps
// the caret (transposing digits, landing in the addon); a plain controlled
// input + @/util/fcc/maskedAmount avoids that while keeping the commas.
function useMaskedAmount(model: Ref<number | null>) {
  const display = ref(model.value == null ? '' : formatAmount(String(model.value)))
  watch(model, value => {
    const next = value == null ? '' : formatAmount(String(value))
    // Skip when the change originated from onInput (display already matches).
    if (parseAmount(display.value) !== value) display.value = next
  })
  function onInput(event: Event) {
    const el = event.target as HTMLInputElement
    const previous = el.value
    const caret = el.selectionStart ?? previous.length
    const formatted = formatAmount(previous)
    display.value = formatted
    model.value = parseAmount(formatted)
    // Set the value + caret imperatively; Vue's :value patch is then a no-op
    // (display already equals the field value) so the caret is preserved.
    el.value = formatted
    const next = restoreCaret(previous, caret, formatted)
    el.setSelectionRange(next, next)
  }
  return { display, onInput }
}

const { display: setReserveMaxDisplay, onInput: onSetReserveMaxInput } =
  useMaskedAmount(setReserveMaxAmount)
const { display: overrideMaxDisplay, onInput: onOverrideMaxInput } =
  useMaskedAmount(overrideMaxAmount)
const { display: reEnableMaxDisplay, onInput: onReEnableMaxInput } =
  useMaskedAmount(reEnableMaxAmount)

const setReserveModeOptions: Array<{ label: string; value: SetReserveMode }> = [
  { label: 'Via Persona', value: 'persona' },
  { label: 'Manual', value: 'manual' },
]

const websiteNumberOptions: Array<{ label: string; value: number }> = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
]

// Helper for seeding currency inputs — the backend returns 0 for null, so
// only a positive value is worth pre-filling.
function isPositiveNumber(value: number | null | undefined): boolean {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
}

// "In the reserve table" is determined by record existence: a 200 from
// /reserve/metrics yields a (non-null) snapshot; a 404 yields null. This is
// the same authoritative signal as the list endpoint's reserve_amount_updatable
// (r.merchant_id is not null), and it matches the RESERVE risk tile so the
// whole page tells one story.
const merchantInReserveTable = computed(
  () => snapshotLoaded.value && snapshotMetrics.value !== null,
)

// Reserve is considered disabled when DISABLED_ON has a value.
// Null/undefined/empty string means the reserve is currently enabled.
const isReserveDisabled = computed(() => {
  const disabledOn = snapshotMetrics.value?.DISABLED_ON
  return disabledOn != null && disabledOn !== ''
})

// Currency the reserve is denominated in, taken from the metrics snapshot. Shown
// in the InputGroup addon beside each amount input so they read e.g. "GBP 50,000".
// The code is kept out of the number field itself (not mode="currency") to avoid
// PrimeVue's caret jump when editing a value with an in-field prefix. When the
// currency is unknown — a brand-new reserve (Set Reserve tab, no snapshot yet) or
// an older row without the column — it is null and the addon is hidden, so the
// field is a bare number rather than a misleading currency.
const reserveCurrency = computed(
  () => snapshotMetrics.value?.DEFAULT_CURRENCY || null,
)

// Tab enable rules — keyed off reserve state. All four tabs are always
// rendered; the inapplicable ones are shown disabled.
//   • Set Reserve      — merchant has no reserve yet (not in table)
//   • Update Max Amount — merchant has an active reserve
//   • Update % Holdback — merchant has an active reserve
//   • Re-enable        — merchant has a reserve that's currently disabled
// The active state enables TWO tabs (Update Max + Update %); the other two
// states enable exactly one. selectInitialTab() picks the leftmost enabled.
const isSetReserveEnabled = computed(() => !merchantInReserveTable.value)
const isOverrideMaxEnabled = computed(
  () => merchantInReserveTable.value && !isReserveDisabled.value,
)
const isUpdatePercentEnabled = computed(
  () => merchantInReserveTable.value && !isReserveDisabled.value,
)
const isReEnableEnabled = computed(
  () => merchantInReserveTable.value && isReserveDisabled.value,
)
const hasAnyAction = computed(
  () =>
    isSetReserveEnabled.value ||
    isOverrideMaxEnabled.value ||
    isUpdatePercentEnabled.value ||
    isReEnableEnabled.value,
)

// Three dialog-body states (keyed off snapshotLoaded, NOT snapshotMetrics —
// a null snapshot is a valid "not in reserve table" result, not an error):
//   • error   — a genuine fetch failure (5xx/network), not yet recovered
//   • loading — fetch in flight / retrying and not yet captured
//   • ready   — snapshot captured (in table or not) → render gated tabs
const showErrorState = computed(
  () =>
    !snapshotLoaded.value &&
    merchantStore.reserveMetricsError &&
    !retrying.value,
)
const showLoadingState = computed(
  () => !snapshotLoaded.value && !showErrorState.value,
)

const submitLabel = computed(() => {
  switch (activeAction.value) {
    case 'overrideMax':
      return 'Update Max Amount'
    case 'updatePercent':
      return 'Update % Holdback'
    case 'reEnable':
      return 'Re-enable Reserve'
    case 'setReserve':
    default:
      return 'Set Reserve'
  }
})

const isValid = computed(() => {
  if (activeAction.value === 'overrideMax') {
    return overrideMaxAmount.value !== null && overrideMaxAmount.value >= 1
  }
  if (activeAction.value === 'updatePercent') {
    return (
      updatePercentHoldback.value !== null &&
      Number.isInteger(updatePercentHoldback.value) &&
      updatePercentHoldback.value >= 1 &&
      updatePercentHoldback.value <= 100
    )
  }
  if (activeAction.value === 'reEnable') {
    // Percent (1–100 integer) and max (≥ 1) are both required.
    return (
      reEnablePercentHoldback.value !== null &&
      Number.isInteger(reEnablePercentHoldback.value) &&
      reEnablePercentHoldback.value >= 1 &&
      reEnablePercentHoldback.value <= 100 &&
      reEnableMaxAmount.value !== null &&
      reEnableMaxAmount.value >= 1
    )
  }
  // setReserve
  if (setReserveMode.value === 'persona') {
    return caseId.value.trim() !== '' && websiteNumber.value !== null
  }
  if (setReserveMode.value === 'manual') {
    return (
      percentHoldback.value !== null &&
      Number.isInteger(percentHoldback.value) &&
      percentHoldback.value >= 1 &&
      percentHoldback.value <= 100 &&
      setReserveMaxAmount.value !== null &&
      setReserveMaxAmount.value >= 1
    )
  }
  return true
})

watch(
  () => merchantStore.isEditReservesModalOpen,
  newValue => {
    isVisible.value = newValue
  },
)

watch(isVisible, newValue => {
  merchantStore.setIsEditReservesModalOpen(newValue)
})

// Lifecycle: reset everything on close; on open, capture the snapshot
// immediately if data is ready, otherwise let the loading watcher capture it
// once the in-flight fetch settles. The two-step approach prevents capturing
// a stale value when the dialog reopens during a post-submit refetch.
watch(isVisible, open => {
  if (!open) {
    snapshotMetrics.value = null
    snapshotLoaded.value = false
    retrying.value = false
    return
  }
  resetForm()
  setReserveMode.value = 'persona'
  maybeCaptureSnapshot()
})

// When loading flips false while the dialog is open and we don't have a
// snapshot yet, capture the fresh metrics. This handles two cases:
//   1. User opens dialog before the initial fetch completes.
//   2. User reopens dialog while a post-submit refetch is still in flight.
watch(
  () => merchantStore.reserveMetricsLoading,
  () => {
    if (isVisible.value) maybeCaptureSnapshot()
  },
)

function maybeCaptureSnapshot() {
  if (snapshotLoaded.value) return // already captured this session
  if (retrying.value) return // retry handler will capture
  if (!merchantStore.reserveMetricsLoaded) return // not settled (loading/errored)
  // Settled successfully: 200 → object (in table), 404 → null (not in table).
  // Capture either way; null is a valid "not in table" snapshot.
  snapshotMetrics.value = merchantStore.reserveMetrics
  snapshotLoaded.value = true
  selectInitialTab()
  seedOverrideMaxFromSnapshot()
  seedUpdatePercentFromSnapshot()
  seedReEnableFromSnapshot()
}

function selectInitialTab() {
  if (isSetReserveEnabled.value) {
    activeAction.value = 'setReserve'
  } else if (isOverrideMaxEnabled.value) {
    activeAction.value = 'overrideMax'
  } else if (isReEnableEnabled.value) {
    activeAction.value = 'reEnable'
  }
}

// Clear inputs whenever the user switches tabs so each tab starts fresh.
watch(activeAction, () => {
  if (!isVisible.value) return
  resetForm()
  seedOverrideMaxFromSnapshot()
  seedUpdatePercentFromSnapshot()
  seedReEnableFromSnapshot()
})

// Clear Set Reserve sub-mode inputs when the user switches between
// Via Persona and Manual.
watch(setReserveMode, () => {
  if (!isVisible.value) return
  caseId.value = ''
  websiteNumber.value = null
  percentHoldback.value = null
  setReserveMaxAmount.value = null
})

function resetForm() {
  caseId.value = ''
  websiteNumber.value = null
  percentHoldback.value = null
  setReserveMaxAmount.value = null
  overrideMaxAmount.value = null
  updatePercentHoldback.value = null
  reEnablePercentHoldback.value = null
  reEnableMaxAmount.value = null
}

function seedOverrideMaxFromSnapshot() {
  // Seed with the current effective cap: prefer the override, fall back to
  // the base max. 0/null are treated as "no value" so the input starts empty.
  const m = snapshotMetrics.value
  const override = isPositiveNumber(m?.OVERRIDE_MAX_AMOUNT)
    ? (m?.OVERRIDE_MAX_AMOUNT as number)
    : null
  const max = isPositiveNumber(m?.MAX_AMOUNT) ? (m?.MAX_AMOUNT as number) : null
  overrideMaxAmount.value = override ?? max
}

function seedUpdatePercentFromSnapshot() {
  // Pre-fill with the current holdback so the operator adjusts from the
  // existing value. 0/null are treated as "no value" so the input starts empty.
  const m = snapshotMetrics.value
  updatePercentHoldback.value = isPositiveNumber(m?.PERCENT_HOLDBACK)
    ? (m?.PERCENT_HOLDBACK as number)
    : null
}

function seedReEnableFromSnapshot() {
  // Pre-fill the re-enable inputs with the reserve's existing values so the
  // operator edits from the current settings. Percent → PERCENT_HOLDBACK; max →
  // effective cap (override if set, else base). 0/null start the input empty.
  const m = snapshotMetrics.value
  reEnablePercentHoldback.value = isPositiveNumber(m?.PERCENT_HOLDBACK)
    ? (m?.PERCENT_HOLDBACK as number)
    : null
  const override = isPositiveNumber(m?.OVERRIDE_MAX_AMOUNT)
    ? (m?.OVERRIDE_MAX_AMOUNT as number)
    : null
  const base = isPositiveNumber(m?.MAX_AMOUNT) ? (m?.MAX_AMOUNT as number) : null
  reEnableMaxAmount.value = override ?? base
}

async function onRetryFetch() {
  const merchantId = merchantStore.currentMerchant?.MERCHANT_ID
  if (merchantId == null) return
  retrying.value = true
  try {
    // Success (200 → object, 404 → null) → capture and render the tabs.
    const metrics = await merchantStore.fetchMerchantReserveMetrics(
      authState,
      merchantId,
    )
    snapshotMetrics.value = metrics
    snapshotLoaded.value = true
    selectInitialTab()
    seedOverrideMaxFromSnapshot()
    seedUpdatePercentFromSnapshot()
    seedReEnableFromSnapshot()
  } catch (err) {
    console.error('Reserve metrics retry failed:', err)
    // Leave snapshotLoaded false so the error state stays visible.
  } finally {
    retrying.value = false
  }
}

// PrimeVue InputNumber only commits v-model on blur. Bind @input so the refs
// update on every keystroke and the submit button enables immediately.
function toInputNumberValue(event: InputNumberInputEvent): number | null {
  const raw = event.value
  if (raw === null || raw === undefined || raw === '') return null
  const parsed = typeof raw === 'number' ? raw : Number(raw)
  return Number.isNaN(parsed) ? null : parsed
}

function onPercentHoldbackInput(event: InputNumberInputEvent) {
  percentHoldback.value = toInputNumberValue(event)
}

function onUpdatePercentHoldbackInput(event: InputNumberInputEvent) {
  updatePercentHoldback.value = toInputNumberValue(event)
}

function onReEnablePercentHoldbackInput(event: InputNumberInputEvent) {
  reEnablePercentHoldback.value = toInputNumberValue(event)
}

function onCancel() {
  isVisible.value = false
}

async function onSubmit() {
  const merchantId = merchantStore.currentMerchant?.MERCHANT_ID
  if (merchantId == null || !isValid.value) return

  const wasAlreadyProcessing = submitting.value
  submitting.value = true
  if (wasAlreadyProcessing) return

  try {
    if (activeAction.value === 'overrideMax') {
      await merchantStore.updateMerchantMaxAmount(
        authState,
        merchantId,
        overrideMaxAmount.value!,
      )
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Max amount updated successfully',
        life: 3000,
      })
      isVisible.value = false
    } else if (activeAction.value === 'updatePercent') {
      const response = await merchantStore.updateMerchantPercentHoldback(
        authState,
        merchantId,
        updatePercentHoldback.value!,
      )
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response?.message || 'Percent holdback updated successfully',
        life: 3000,
      })
      isVisible.value = false
    } else if (activeAction.value === 'reEnable') {
      // Re-enable and set the (pre-filled or edited) percent + max in one call
      // via PATCH /reserve/enable with an optional body.
      const response = await merchantStore.enableMerchantReserve(
        authState,
        merchantId,
        {
          percent_holdback: reEnablePercentHoldback.value!,
          max_amount: reEnableMaxAmount.value!,
        },
      )
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response?.message || 'Reserve re-enabled successfully',
        life: 3000,
      })
      isVisible.value = false
    } else if (setReserveMode.value === 'persona') {
      const response = await merchantStore.setMerchantReservePersona(
        authState,
        merchantId,
        {
          persona_case_id: caseId.value.trim(),
          website_number: websiteNumber.value!,
        },
      )
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response?.message || 'Reserve set from Persona case',
        life: 3000,
      })
      isVisible.value = false
    } else {
      // setReserveMode === 'manual'
      const response = await merchantStore.setMerchantReserveManual(
        authState,
        merchantId,
        {
          percent_holdback: percentHoldback.value!,
          max_amount: setReserveMaxAmount.value!,
        },
      )
      toast.add({
        severity: 'success',
        summary: 'Success',
        detail: response?.message || 'Reserve set manually',
        life: 3000,
      })
      isVisible.value = false
    }

    // Refresh the on-page Reserve Metrics tiles so the user sees the new
    // values without navigating away. Fire-and-forget — the user already
    // got the success toast; a refresh failure is non-blocking.
    void merchantStore
      .fetchMerchantReserveMetrics(authState, merchantId)
      .catch(refreshErr => {
        console.error(
          'Failed to refresh reserve metrics after submit:',
          refreshErr,
        )
      })
  } catch (error) {
    console.error('Failed to submit Edit Reserves action:', error)
    const apiError = normalizeError(error)
    errorToastWithDetails(toast, 'Error', apiError.toToastMessage(), apiError)
  } finally {
    submitting.value = false
  }
}
</script>
